import {sendAnalyticsEvent} from '#/analytics/analytics';
import {ExpandableTile} from '#/atoms/expandable-tile';
import {
  transactionActivityTileProps,
  transactionTypeConstants,
} from '#/components/transaction/transaction-type-constants';
import {useLands} from '#/hooks/query/use-lands';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {useSelectedLand} from '#/hooks/use-selected-land';
import {useTransactionDate} from '#/hooks/use-transaction-date';
import {ClassName} from '#/utils/utils';
import {InformationCircleIcon} from '@heroicons/react/24/outline';
import {DisplayableShareLandTransaction, Land} from '@shareland/shareland-common';
import {FunctionComponent, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';

type TransactionTileProps = {
  land: Land;
  transaction: DisplayableShareLandTransaction;
};

export const TransactionTileSmall: FunctionComponent<Omit<TransactionTileProps, 'land'>> = ({transaction}) => {
  const {i18nKey} = transactionTypeConstants[transaction.type];
  const {IconComponent, iconColorClassName, bgColor, primaryTextColor, secondaryTextColor} =
    transactionActivityTileProps[transaction.type];

  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();
  const {data: lands} = useLands();

  const land = lands?.find(land => land.id === transaction.landId);

  return (
    <div
      className={`${bgColor} w-full @xl/transaction-section:w-[160px] text-sm font-bold grid grid-rows-2 grid-cols-transactionTileSmallTwoLines gap-x-3 @xl/transaction-section:block rounded-xl px-2.5 py-1 @xl/transaction-section:py-2`}
    >
      <span className={`${primaryTextColor} flex items-center gap-1`}>
        <IconComponent className={`inline ${iconColorClassName} w-4 h-4`} />
        {t(`transactions.${i18nKey}.past`)}
      </span>
      <span className={`block ${secondaryTextColor} whitespace-nowrap col-start-1 row-start-2 truncate`}>
        {land?.tokenSymbol ?? 'Unknown Land'}
      </span>
      <span
        className={`block ${secondaryTextColor} whitespace-nowrap text-right row-start-2 @xl/transaction-section:text-left`}
      >
        {t('utils.stableTokenAmount', {
          landTokenAmount: transaction.raw.landTokenAmount,
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(transaction.raw.stableTokenAmount),
        })}
      </span>
    </div>
  );
};

export const TransactionTileSmallLoading: FunctionComponent<ClassName & {index: number}> = ({className, index}) => {
  return (
    <div
      style={{animationDelay: index * 0.1 + 's'}}
      className={`animate-pulse w-full @xl/transaction-section:w-[160px] bg-gray-400 text-sm font-bold flex gap-3 @xl/transaction-section:block items-center rounded-xl px-2.5 py-1 @xl/transaction-section:py-2 ${className}`}
    >
      <span className={'flex items-center gap-1'}> </span>
      <span className={'block  whitespace-nowrap truncate'}> </span>
      <span className={'block  whitespace-nowrap'}> </span>
    </div>
  );
};

export const TransactionTileRegular: FunctionComponent<TransactionTileProps> = ({transaction, land}) => {
  const {i18nKey} = transactionTypeConstants[transaction.type];
  const {IconComponent, iconColorClassName, bgColor, primaryTextColor, secondaryTextColor} =
    transactionActivityTileProps[transaction.type];

  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <div className={`${bgColor} text-sm  font-bold flex justify-between items-center rounded-xl p-4`}>
      <div className={'grow'}>
        <span className={`block ${primaryTextColor}`}>
          {t('components.transactionTile.actionLand', {
            action: t(`transactions.${i18nKey}.past`),
            landTokenSymbol: land.tokenSymbol,
          })}
        </span>
        <span className={`block ${secondaryTextColor}`}>
          {t('components.transactionTile.amount', {
            landTokenAmount: transaction.raw.landTokenAmount,
            stableCurrencySymbol: displayPref.symbol,
            stableTokenAmount: displayPref.displayAmount(transaction.raw.stableTokenAmount),
          })}
        </span>
      </div>
      <IconComponent className={`inline ${iconColorClassName} w-6 h-6`} />
    </div>
  );
};

export const TransactionTileLarge: FunctionComponent<TransactionTileProps> = ({transaction, land}) => {
  const {i18nKey} = transactionTypeConstants[transaction.type];
  const {IconComponent, iconColorClassName} = transactionActivityTileProps[transaction.type];

  const {t} = useTranslation('translation');
  const {
    actionMenu: {openInformation},
  } = useQueryNavigationActions();

  const {data: selectedLand} = useSelectedLand();
  const displayPref = useCoinDisplayPreferences();
  const transactionDate = useTransactionDate(transaction);

  const isSelected = !!selectedLand && selectedLand.id === land.id;
  const primaryTextColor = 'text-white';
  const secondaryTextColor = 'text-white/70';

  const onInformationClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    openInformation(land);
  };

  const onExpand = () => {
    sendAnalyticsEvent.previousTransaction({
      name: land.name,
      transaction_type: i18nKey,
      transaction_land_token_amount: t('utils.nativeAmount', {
        amount: transaction.raw.landTokenAmount,
      }),
      transaction_stable_token_amount: t('utils.stableAmount', {
        amount: transaction.raw.stableTokenAmount,
      }),
      transaction_date: transactionDate ? transactionDate.toLocaleString() : '',
    });
  };

  return (
    <ExpandableTile
      onExpand={onExpand}
      data-selected={isSelected}
      className='border data-[selected=true]:border-deepblue data-[selected=false]:border-grayblue transition-all'
    >
      {{
        content: (
          <div
            className='flex gap-5 py-5 data-[selected=false]:cursor-pointer group/transaction-tile-large-content'
            data-selected={isSelected}
            onClick={onInformationClick}
          >
            <span className='self-center'>
              <IconComponent className={`inline ${iconColorClassName} w-6 h-6`} />
            </span>

            <div className='grow flex justify-between items-stretch font-bold'>
              <div className='grow'>
                <span className={`block text-xs ${secondaryTextColor} mb-1`}>
                  {!!transactionDate ? (
                    t('components.transactionTile.date', {
                      date: transactionDate,
                    })
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </span>
                <div className={`flex items-center gap-2 ${primaryTextColor} transition-all`}>
                  {t('components.transactionTile.actionLand', {
                    action: t(`transactions.${i18nKey}.past`),
                    landTokenSymbol: land.tokenSymbol,
                  })}
                  <InformationCircleIcon className='w-6 h-6 fill-transparent stroke-gray-400 hidden opacity-0 group-data-[selected=false]/transaction-tile-large-content:group-hover/transaction-tile-large-content:block group-data-[selected=false]/transaction-tile-large-content:group-hover/transaction-tile-large-content:opacity-100 transition-all' />
                </div>
                <span className={`block ${secondaryTextColor}`}>
                  {t('components.transactionTile.amount', {
                    landTokenAmount: transaction.raw.landTokenAmount,
                    stableCurrencySymbol: displayPref.symbol,
                    stableTokenAmount: displayPref.displayAmount(transaction.raw.stableTokenAmount),
                  })}
                </span>
              </div>
            </div>
          </div>
        ),
        expandedContentTitle: t('components.transactionTile.expandedContentTitle'),
        expandedContent: (
          <div className='w-full max-w-full flex flex-col flex-wrap gap-3'>
            <ExpandedTileRow primaryTextColor={primaryTextColor} secondaryTextColor={secondaryTextColor}>
              {{
                title: t('components.transactionTile.infoRows.blockNumber.title'),
                content: t('components.transactionTile.infoRows.blockNumber.value', {
                  value: transaction.blockNumber,
                }),
              }}
            </ExpandedTileRow>

            <ExpandedTileRow primaryTextColor={primaryTextColor} secondaryTextColor={secondaryTextColor}>
              {{
                title: t('components.transactionTile.infoRows.transactionHash.title'),
                content: t('components.transactionTile.infoRows.transactionHash.value', {
                  transactionHash: transaction.transactionHash,
                }),
              }}
            </ExpandedTileRow>

            {transaction.raw.transactionFees && (
              <ExpandedTileRow primaryTextColor={primaryTextColor} secondaryTextColor={secondaryTextColor}>
                {{
                  title: t('components.transactionTile.infoRows.transactionFees.title'),
                  content: t('components.transactionTile.infoRows.transactionFees.value', {
                    currencySymbol: displayPref.symbol,
                    amount: displayPref.displayAmount(transaction.raw.transactionFees),
                  }),
                }}
              </ExpandedTileRow>
            )}
          </div>
        ),
      }}
    </ExpandableTile>
  );
};

const ExpandedTileRow: FunctionComponent<{
  primaryTextColor: string;
  secondaryTextColor: string;
  children: {title: ReactNode; content: ReactNode};
}> = ({primaryTextColor, secondaryTextColor, children: {title, content}}) => (
  <div className='w-full flex flex-col text-sm overflow-hidden'>
    <span className={`${primaryTextColor} font-bold truncate`}>{title}</span>
    <span className={`${secondaryTextColor} font-semibold truncate`}>{content}</span>
  </div>
);
